/**
 * useAboveUCitations
 *
 * MINSKY-17131: aggregate every above-(U) citation currently in the document, live from
 * Y.Map("referenceMarks") (no Firestore/REST round-trip — the Y.Doc is already the source of
 * truth for citations while the editor is open). Shared by the "Find (U) Alternatives" left-menu
 * badge count (AIAssistant.jsx) and the whole-document view (AboveUCitationsView.jsx) so both
 * always agree on the same count.
 *
 * Classification resolution mirrors the margin-rail badge: prefer the citation's own
 * `docClassificationLevel` (fast path for citations added after MINSKY-17131 shipped); for older
 * citations missing it, batch-resolve via the shared classification cache (one
 * `GET /api/documents/{docId}` per unique docId, ever).
 *
 * Dedup: the same citation (same `localId`) can be attached to several reference marks
 * (i.e. cited in several places in the report). Those are collapsed into a single entry
 * carrying every owning `referenceMarkId` (as `referenceMarkIds`) plus a `usageCount`, so the
 * whole-document view shows one row per distinct source instead of one per usage.
 */

import {useEffect, useMemo, useState} from 'react';
import useYDocStore from '../stores/useYDocStore';
import {getAllReferences, observeReferences} from '../components/prosemirror-editor/yMaps/referencesYMap';
import {isAboveU} from '../components/prosemirror-editor/helpers/documentClassification';
import {resolveClassificationLevels} from './useDocumentClassificationCache';

/**
 * @returns {{
 *   citations: Array<Object & {
 *     referenceMarkId: string,
 *     referenceMarkIds: string[],
 *     usageCount: number,
 *     resolvedClassificationLevel: string,
 *   }>,
 *   loading: boolean,
 * }}
 */
export function useAboveUCitations() {
    const yReferenceMarks = useYDocStore((s) => s.yReferenceMarks);
    const [version, setVersion] = useState(0);
    const [backfilled, setBackfilled] = useState(() => new Map());

    useEffect(() => {
        if (!yReferenceMarks) return undefined;
        return observeReferences(yReferenceMarks, () => setVersion((v) => v + 1));
    }, [yReferenceMarks]);

    // Flatten every mark's references, tagging each with its owning referenceMarkId.
    const allReferences = useMemo(() => {
        if (!yReferenceMarks) return [];
        const byMark = getAllReferences(yReferenceMarks);
        const flat = [];
        byMark.forEach((refs, referenceMarkId) => {
            refs.forEach((ref) => flat.push({...ref, referenceMarkId}));
        });
        return flat;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [yReferenceMarks, version]);

    // Backfill classification for citations missing docClassificationLevel, batched by unique docId.
    useEffect(() => {
        const missingDocIds = allReferences
            .filter((ref) => !ref.docClassificationLevel && ref.docId != null)
            .map((ref) => ref.docId);
        const uniqueMissing = Array.from(new Set(missingDocIds.map(String)))
            .filter((id) => !backfilled.has(id));
        if (uniqueMissing.length === 0) return;

        let cancelled = false;
        resolveClassificationLevels(uniqueMissing).then((resolved) => {
            if (cancelled) return;
            setBackfilled((prev) => {
                const next = new Map(prev);
                resolved.forEach((level, docId) => next.set(docId, level));
                return next;
            });
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allReferences]);

    const citations = useMemo(() => {
        const aboveU = allReferences
            .map((ref) => {
                const resolvedClassificationLevel = ref.docClassificationLevel
                    ?? (ref.docId != null ? backfilled.get(String(ref.docId)) ?? null : null);
                return {...ref, resolvedClassificationLevel};
            })
            // MINSKY-17131 fix: docId is intentionally null for non-corpus citations
            // (My Data / Report / Highlight — see unifiedHitToSegmentProps.js), so it must
            // not gate inclusion here. Those citations still carry a valid
            // docClassificationLevel and the downstream alternative-search flow keys off
            // localId, not docId.
            .filter((ref) => isAboveU(ref.resolvedClassificationLevel));

        // Dedup by localId — the same source cited in multiple places collapses into one
        // entry carrying every owning referenceMarkId, so the UI shows/acts on it once.
        const byLocalId = new Map();
        aboveU.forEach((ref) => {
            const key = ref.localId ?? ref.referenceMarkId;
            const existing = byLocalId.get(key);
            if (existing) {
                existing.referenceMarkIds.push(ref.referenceMarkId);
            } else {
                byLocalId.set(key, {...ref, referenceMarkIds: [ref.referenceMarkId]});
            }
        });

        return Array.from(byLocalId.values()).map((ref) => ({
            ...ref,
            usageCount: ref.referenceMarkIds.length,
        }));
    }, [allReferences, backfilled]);

    const stillBackfilling = allReferences.some(
        (ref) => !ref.docClassificationLevel && ref.docId != null && !backfilled.has(String(ref.docId))
    );

    return {citations, loading: stillBackfilling};
}

export default useAboveUCitations;

